import React, { useContext, useEffect, useMemo, useState } from "react";
import { gql, useQuery } from "@apollo/client";
import StarekContext from "../../context/StarekContext";
import { GET_REPORT_SHIFTS, fetchDatapointsForRange } from "../report/phase1/reportData";
import {
    achievement,
    buildDowntimeByDevice,
    buildTransitionsByDevice,
    businessAlerts,
    changeVs,
    downtimeSeconds,
    maintenanceSummary,
    periodTarget,
    productionTrend,
    sameTimeYesterday,
    startOfDay,
    totalProduction,
    SEVERITY,
} from "./ownerDashboardData";
import {
    AlertsCard,
    KpiCard,
    MaintenanceCard,
    PerformanceTable,
    TrendCard,
    fmtHours,
    fmtPcs,
} from "./OwnerDashboardCards";
import "./ownerDashboard.css";

// Owner / Leadership Dashboard (phase 1): a plant-level summary for users who
// hold the Owner role on the current plant. Reads only what the user can
// already read — GraphQL reference data plus ONE datapoints call covering the
// last 7 days, from which today, yesterday and the trend are all derived.

const GET_OWNER_DEVICES = gql`
    query GetOwnerDashboardDevices($where: ShadowDeviceWhereInput) {
        shadowDevices(where: $where) {
            id
            name
            device
            parameters
        }
    }
`;

const GET_OWNER_JOBS = gql`
    query GetOwnerDashboardJobs($where: JobScheduleWhereInput!) {
        jobSchedules(where: $where) {
            id
            name
            target_units
            start_time
            end_time
            device_id
            part {
                id
                name
            }
            operations {
                device_id
                sequence_no
            }
            shift {
                id
                start_time
                end_time
                runs_on
            }
        }
    }
`;

const GET_OWNER_MAINTENANCE = gql`
    query GetOwnerDashboardMaintenance($where: MaintenanceScheduleWhereInput!) {
        maintenanceSchedules(where: $where) {
            id
            name
            schedule_type
            schedule_attributes
            is_active
            deleted_at
            device_id
            device {
                id
                name
            }
        }
    }
`;

function OwnerDashboard() {
    const starekContext = useContext(StarekContext);
    const { engineClient, lastUsedWorkspace, lastUsedPlant, auth, types } = starekContext;
    const plantId = lastUsedPlant?.id ? Number(lastUsedPlant.id) : null;

    // Every figure on the page is computed against this one instant, so the
    // cards agree with each other. Refresh moves it forward.
    const [nowMs, setNowMs] = useState(() => Date.now());
    const todayStart = startOfDay(nowMs);
    const yesterdayStart = startOfDay(todayStart - 1);
    // Today is only partly over, so it is compared with the same elapsed span
    // of yesterday; a full yesterday would make every morning look like a drop.
    const yesterdayEnd = sameTimeYesterday(nowMs);
    // Start of the day 6 days ago: 7 calendar days, today included.
    const dataStart = useMemo(() => {
        const d = new Date(todayStart);
        d.setDate(d.getDate() - 6);
        return d.getTime();
    }, [todayStart]);

    const queryOpts = {
        client: engineClient,
        fetchPolicy: "network-only",
        skip: !plantId,
    };

    const devicesQ = useQuery(GET_OWNER_DEVICES, {
        ...queryOpts,
        variables: {
            where: { plant_id: { equals: plantId }, is_active: { equals: true } },
        },
    });
    const jobsQ = useQuery(GET_OWNER_JOBS, {
        ...queryOpts,
        variables: {
            where: {
                plant_id: { equals: plantId },
                deleted_at: { equals: null },
                start_time: { lte: new Date(nowMs).toISOString() },
                end_time: { gte: new Date(dataStart).toISOString() },
            },
        },
    });
    const shiftsQ = useQuery(GET_REPORT_SHIFTS, {
        ...queryOpts,
        variables: { plantId },
    });
    const maintQ = useQuery(GET_OWNER_MAINTENANCE, {
        ...queryOpts,
        variables: {
            where: {
                plant_id: { equals: plantId },
                deleted_at: { equals: null },
                is_active: { equals: true },
            },
        },
    });

    const devices = useMemo(() => devicesQ.data?.shadowDevices || [], [devicesQ.data]);
    const jobs = useMemo(() => jobsQ.data?.jobSchedules || [], [jobsQ.data]);
    const shifts = useMemo(() => shiftsQ.data?.shifts || [], [shiftsQ.data]);
    const schedules = useMemo(
        () => maintQ.data?.maintenanceSchedules || [],
        [maintQ.data],
    );

    // ---- Datapoints: one call for every plant device, 7 days -> now ----
    const [datapoints, setDatapoints] = useState(null);
    const [dpLoading, setDpLoading] = useState(false);
    const [dpError, setDpError] = useState(null);
    const deviceKey = devices.map((d) => d.id).join(",");

    useEffect(() => {
        if (!lastUsedWorkspace || devicesQ.loading) return;
        if (!deviceKey) {
            setDatapoints(null);
            return;
        }
        let cancelled = false;
        setDpLoading(true);
        setDpError(null);
        fetchDatapointsForRange({
            workspace: lastUsedWorkspace,
            authToken: auth,
            deviceIds: deviceKey.split(","),
            startTime: Math.floor(dataStart / 1000),
            endTime: Math.floor(nowMs / 1000),
        })
            .then((data) => !cancelled && setDatapoints(data))
            .catch((err) => {
                if (!cancelled) {
                    setDatapoints(null);
                    setDpError(err);
                }
            })
            .finally(() => !cancelled && setDpLoading(false));
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deviceKey, devicesQ.loading, dataStart, nowMs, lastUsedWorkspace?.name]);

    const refresh = () => {
        setNowMs(Date.now());
        devicesQ.refetch?.();
        shiftsQ.refetch?.();
        maintQ.refetch?.();
    };

    // ---- Derived figures ----
    const transitionsByDevice = useMemo(
        () => buildTransitionsByDevice(devices, datapoints, types),
        [devices, datapoints, types],
    );
    const downtimeByDevice = useMemo(
        () =>
            buildDowntimeByDevice(
                devices,
                datapoints,
                types,
                Math.floor(dataStart / 1000),
                Math.floor(nowMs / 1000),
            ),
        [devices, datapoints, types, dataStart, nowMs],
    );

    const queryLoading =
        devicesQ.loading || jobsQ.loading || shiftsQ.loading || maintQ.loading;
    // Devices are known but their datapoints have not arrived yet (the fetch
    // effect has not started, or is about to): still loading, not "zero".
    const dpPending =
        Boolean(deviceKey) && datapoints == null && !dpError && !!lastUsedWorkspace;
    // Telemetry-backed figures are not ready until the datapoints are in.
    const telemetryLoading = queryLoading || dpLoading || dpPending;
    const error =
        devicesQ.error || jobsQ.error || shiftsQ.error || maintQ.error || dpError;

    // What could not be loaded. Figures built on it show "Unavailable" instead
    // of zeros computed from empty data, which would read as real results.
    const telemetryUnavailable =
        !telemetryLoading &&
        Boolean(dpError || devicesQ.error || (deviceKey && datapoints == null));
    const productionUnavailable = telemetryUnavailable || Boolean(jobsQ.error);
    const targetUnavailable = Boolean(jobsQ.error);
    const downtimeUnavailable = telemetryUnavailable || Boolean(shiftsQ.error);

    const figures = useMemo(() => {
        const today = [todayStart, nowMs];
        const yesterday = [yesterdayStart, yesterdayEnd];
        const maint = maintenanceSummary(schedules, types, nowMs);
        return {
            achievementToday: achievement(jobs, transitionsByDevice, ...today),
            productionToday: totalProduction(jobs, transitionsByDevice, ...today),
            productionYesterday: totalProduction(jobs, transitionsByDevice, ...yesterday),
            targetToday: periodTarget(jobs, ...today),
            targetYesterday: periodTarget(jobs, ...yesterday),
            downtimeToday: downtimeSeconds(downtimeByDevice, shifts, ...today),
            downtimeYesterday: downtimeSeconds(downtimeByDevice, shifts, ...yesterday),
            trend: productionTrend(jobs, transitionsByDevice, dataStart, nowMs),
            maint,
            alerts: businessAlerts({
                jobs,
                devices,
                shifts,
                transitionsByDevice: productionUnavailable ? null : transitionsByDevice,
                downtimeByDevice: downtimeUnavailable ? null : downtimeByDevice,
                underMaintenance: maint.underMaintenance,
                nowMs,
                dataStartMs: dataStart,
            }),
        };
    }, [
        jobs,
        devices,
        shifts,
        schedules,
        types,
        transitionsByDevice,
        downtimeByDevice,
        todayStart,
        yesterdayStart,
        yesterdayEnd,
        dataStart,
        nowMs,
        productionUnavailable,
        downtimeUnavailable,
    ]);

    // ---- KPI values ----
    const ach = figures.achievementToday;
    const noShifts = !shiftsQ.loading && !shiftsQ.error && shifts.length === 0;
    const alertsPartial = productionUnavailable || downtimeUnavailable;
    const highCount = figures.alerts.filter((a) => a.severity === SEVERITY.HIGH).length;
    const mediumCount = figures.alerts.length - highCount;
    const downtimeChange = changeVs(figures.downtimeToday, figures.downtimeYesterday, {
        lowerIsBetter: true,
    });
    const dash = "…";
    const na = (label, text) => ({ label, today: text, yesterday: text, na: true });

    const downtimeSub = () => {
        if (downtimeUnavailable) return "Data unavailable";
        if (noShifts) return "Downtime is measured within shift hours";
        if (figures.downtimeYesterday == null) return null;
        if (!downtimeChange) return `This time yesterday ${fmtHours(figures.downtimeYesterday)}`;
        const word = downtimeChange.direction === "down" ? "less" : "more";
        if (downtimeChange.direction === "flat") return "Same as this time yesterday";
        return `${Math.abs(downtimeChange.pct).toFixed(0)}% ${word} than this time yesterday (${fmtHours(figures.downtimeYesterday)})`;
    };

    const perfRows = [
        productionUnavailable
            ? na("Total Production", "Unavailable")
            : {
                  label: "Total Production",
                  today: fmtPcs(figures.productionToday),
                  yesterday: fmtPcs(figures.productionYesterday),
                  change: changeVs(figures.productionToday, figures.productionYesterday),
              },
        targetUnavailable
            ? na("Target", "Unavailable")
            : {
                  label: "Target",
                  today: fmtPcs(figures.targetToday),
                  yesterday: fmtPcs(figures.targetYesterday),
                  change: changeVs(figures.targetToday, figures.targetYesterday),
                  tone: "neutral",
              },
        downtimeUnavailable
            ? na("Downtime", "Unavailable")
            : noShifts
              ? na("Downtime", "No shifts configured")
              : {
                    label: "Downtime",
                    today: fmtHours(figures.downtimeToday || 0),
                    yesterday: fmtHours(figures.downtimeYesterday || 0),
                    change: downtimeChange,
                },
        { label: "Energy Consumption", today: "Not configured", yesterday: "Not configured", na: true },
    ];

    // Gaps worth saying out loud, so an empty card is not mistaken for "all good".
    const notices = [];
    if (!queryLoading && !error) {
        if (!devices.length) notices.push("This plant has no active machines yet.");
        if (!jobs.length)
            notices.push(
                "No job schedules in the last 7 days — production and targets are counted from jobs.",
            );
        if (noShifts) notices.push("No shifts configured — downtime is measured within shift hours.");
    }

    return (
        <section className="content od">
            <div className="container-fluid">
                <div className="od-head">
                    <div>
                        <h1>Owner / Leadership Dashboard (Executive View)</h1>
                        <p>
                            {lastUsedPlant?.name || "Current plant"} · as of{" "}
                            {new Date(nowMs).toLocaleString(undefined, {
                                day: "2-digit",
                                month: "short",
                                hour: "2-digit",
                                minute: "2-digit",
                            })}
                        </p>
                    </div>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={refresh}
                        disabled={telemetryLoading}
                    >
                        <i className={`fas fa-sync mr-1 ${telemetryLoading ? "fa-spin" : ""}`} />
                        Refresh
                    </button>
                </div>

                {error && (
                    <div className="alert alert-danger d-flex justify-content-between align-items-center">
                        <span>
                            <i className="fa fa-exclamation-triangle mr-2" />
                            Could not load dashboard data: {error.message}
                        </span>
                        <button type="button" className="btn btn-sm btn-light" onClick={refresh}>
                            Retry
                        </button>
                    </div>
                )}

                {notices.length > 0 && (
                    <div className="alert alert-light border text-muted py-2" style={{ fontSize: 13 }}>
                        {notices.map((n) => (
                            <div key={n}>
                                <i className="fa fa-info-circle mr-2" />
                                {n}
                            </div>
                        ))}
                    </div>
                )}

                {/* ── KPI boxes ─────────────────────────────────────────── */}
                <div className="row">
                    <KpiCard
                        icon="fas fa-tachometer-alt"
                        theme="primary"
                        label="Overall OEE"
                        value="—"
                        sub="Needs availability, performance and quality inputs"
                        muted
                    />
                    <KpiCard
                        icon="fa fa-bullseye"
                        theme="success"
                        label="Production Achievement"
                        to="/reports-v2"
                        value={
                            telemetryLoading
                                ? dash
                                : productionUnavailable || ach.pct == null
                                  ? "—"
                                  : `${Math.round(ach.pct)}%`
                        }
                        sub={
                            telemetryLoading
                                ? null
                                : productionUnavailable
                                  ? "Data unavailable"
                                  : ach.target > 0
                                  ? `${ach.actual.toLocaleString()} / ${fmtPcs(ach.target)} today`
                                  : "No target set for today"
                        }
                    />
                    <KpiCard
                        icon="fa fa-pause-circle"
                        theme="warning"
                        label="Downtime Today"
                        to="/reports-v2"
                        value={
                            telemetryLoading
                                ? dash
                                : downtimeUnavailable
                                  ? "—"
                                  : noShifts
                                    ? "No shifts"
                                    : fmtHours(figures.downtimeToday || 0)
                        }
                        sub={telemetryLoading ? null : downtimeSub()}
                    />
                    <KpiCard
                        icon="fas fa-exclamation-triangle"
                        theme="purple"
                        label="Business Alerts"
                        linkText="See Top Alerts below"
                        footerIcon="fas fa-arrow-circle-down"
                        value={telemetryLoading ? dash : figures.alerts.length}
                        extra={
                            telemetryLoading ? null : (
                                <span className="od-box-dots">
                                    <span><i className="od-dot od-dot-high" />{highCount} High</span>
                                    <span><i className="od-dot od-dot-medium" />{mediumCount} Medium</span>
                                    {alertsPartial && <span>· partial</span>}
                                </span>
                            )
                        }
                    />
                </div>

                <div className="row">
                    <div className="col-lg-7 mb-3">
                        <PerformanceTable rows={perfRows} loading={telemetryLoading} />
                    </div>
                    <div className="col-lg-5 mb-3">
                        <AlertsCard
                            alerts={figures.alerts}
                            loading={telemetryLoading}
                            partial={alertsPartial}
                        />
                    </div>
                </div>

                <div className="row">
                    <div className="col-lg-7 mb-3">
                        <TrendCard
                            trend={figures.trend}
                            loading={telemetryLoading}
                            unavailable={productionUnavailable}
                        />
                    </div>
                    <div className="col-lg-5 mb-3">
                        <MaintenanceCard
                            upcoming={figures.maint.upcoming}
                            underMaintenance={figures.maint.underMaintenance}
                            loading={maintQ.loading}
                        />
                    </div>
                </div>
            </div>
        </section>
    );
}

export default OwnerDashboard;
